import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    CreditCard,
    Loader, Download,
} from "lucide-react";
import {
    getManagerAvanceInboxRequest,
    managerAvanceDecisionRequest,
    getManagerAvanceHistoryRequest,
    exportAvanceRequestsExcelRequest,
} from "../../../../redux/actions/avanceRequestActions";
import useTreatmentDelegation from "./useTreatmentDelegation";
import "../TraitementStyles/TraitementAvances.css";

const TraitementAvancesManager = () => {
    const dispatch = useDispatch();

    // ✅ Redux state adapté pour manager
    const { managerInbox, managerHistory, loading } = useSelector((state) => state.avanceRequests);

    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const [filters, setFilters] = useState({
        typeDemande: "",
        montantMin: "",
        montantMax: "",
        dateDebut: "",
        dateFin: "",
    });

    const delegation = useTreatmentDelegation({
        endpoint: "/api/avance-requests/manager/treatment-delegation",
        label: "d'avances et prêts",
        onUpdated: () => {
            dispatch(getManagerAvanceInboxRequest());
            dispatch(getManagerAvanceHistoryRequest());
        },
    });

    // ✅ Charger données manager au montage
    useEffect(() => {
        dispatch(getManagerAvanceInboxRequest());
        dispatch(getManagerAvanceHistoryRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant) => {
        if (!montant) return "0";
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 0
        }).format(montant);
    };

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "Transmis à la RH", bg: "#d1ecf1", color: "#0c5460" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.PENDING_MANAGER;
        return (
            <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const handleApprove = (demandeId) => {
        dispatch(managerAvanceDecisionRequest({
            id: demandeId,
            decision: true,
            comment: ""
        }));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;

        dispatch(managerAvanceDecisionRequest({
            id: selectedDemande.id,
            decision: false,
            comment: motifRejet
        }));

        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    // ✅ Mapper les statuts vers les onglets (manager)
    const mapStatutToTab = (statut) => {
        if (statut === "PENDING_MANAGER") return "enCours";
        if (statut === "APPROVED") return "approuve";
        if (statut === "REJECTED") return "rejete";
        return "enCours";
    };

    // ✅ Fusionner inbox + history
    const allDemandes = useMemo(() => {
        return [...(managerInbox || []), ...(managerHistory || [])];
    }, [managerInbox, managerHistory]);

    const getFilteredDemandes = () => {
        if (!allDemandes || allDemandes.length === 0) return [];

        let filtered = allDemandes.filter((d) => mapStatutToTab(d.status) === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.employeeName && d.employeeName.toLowerCase().includes(s)) ||
                    (d.employeeMatricule && d.employeeMatricule.toLowerCase().includes(s)) ||
                    (d.avanceType && d.avanceType.toLowerCase().includes(s))
            );
        }

        if (filters.typeDemande) {
            filtered = filtered.filter((d) => d.avanceType === filters.typeDemande);
        }

        if (filters.montantMin) {
            filtered = filtered.filter((d) => d.montantDemande >= Number(filters.montantMin));
        }

        if (filters.montantMax) {
            filtered = filtered.filter((d) => d.montantDemande <= Number(filters.montantMax));
        }

        if (filters.dateDebut) {
            filtered = filtered.filter((d) => {
                const dateCreation = new Date(d.createdAt).toISOString().split('T')[0];
                return dateCreation >= filters.dateDebut;
            });
        }

        if (filters.dateFin) {
            filtered = filtered.filter((d) => {
                const dateCreation = new Date(d.createdAt).toISOString().split('T')[0];
                return dateCreation <= filters.dateFin;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        if (!allDemandes) return [
            { id: "enCours", label: "En cours", count: 0 },
            { id: "approuve", label: "Approuvées", count: 0 },
            { id: "rejete", label: "Rejetées", count: 0 },
        ];

        return [
            {
                id: "enCours",
                label: "En cours",
                count: allDemandes.filter((d) => d.status === "PENDING_MANAGER" || d.status === "PENDING_ADMIN").length
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: allDemandes.filter((d) => d.status === "APPROVED").length
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: allDemandes.filter((d) => d.status === "REJECTED").length
            },
        ];
    }, [allDemandes]);

    const typesDemandes = useMemo(() => {
        if (!allDemandes) return [];
        return [...new Set(allDemandes.map((d) => d.avanceType).filter(Boolean))];
    }, [allDemandes]);

    const filtered = getFilteredDemandes();

    if (loading && allDemandes.length === 0) {
        return (
            <div className="tavPage">
                <div className="tavWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tavPage">
            <div className="tavWrap">
                {/* Header */}
                <div className="tavHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>Traitement des demandes d'avances et prêts</h1>
                        <p>Validez les demandes d'avances sur salaire et prêts de vos collaborateurs</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">Délégation Admin RH</div>
                            <div className="tavDelegationSetting__sub">Transmettre mon traitement d'avances à l'admin RH</div>
                        </div>
                        <label className="tavSwitch" title="Délégation Admin RH">
                            <input
                                type="checkbox"
                                checked={delegation.enabled}
                                disabled={delegation.loading}
                                onChange={(e) => delegation.toggle(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tavCard">
                    <div className="tavControls">
                        <div className="tavSearch">
                            <Search size={18}/>
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button type="button" className="tavFilterBtn" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16}/>
                            Filtres
                            <ChevronDown size={16} className={`tavChevron ${showFilters ? "is-open" : ""}`}/>
                        </button>
                        <button
                            type="button"
                            className="tavFilterBtn"
                            style={{marginLeft: "auto", background: "#10b981", color: "white", border: "none"}}
                            onClick={() => dispatch(exportAvanceRequestsExcelRequest("manager_inbox"))}
                        >
                            <Download size={16}/>
                            Exporter Excel
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tavFilters">
                            <div className="tavField">
                                <label>Type de demande</label>
                                <select value={filters.typeDemande}
                                        onChange={(e) => setFilters((p) => ({...p, typeDemande: e.target.value}))}>
                                <option value="">Tous</option>
                                    {typesDemandes.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="tavField">
                                <label>Montant min (dhs)</label>
                                <input
                                    type="number"
                                    value={filters.montantMin}
                                    onChange={(e) => setFilters((p) => ({ ...p, montantMin: e.target.value }))}
                                    placeholder="0"
                                />
                            </div>

                            <div className="tavField">
                                <label>Montant max (dhs)</label>
                                <input
                                    type="number"
                                    value={filters.montantMax}
                                    onChange={(e) => setFilters((p) => ({ ...p, montantMax: e.target.value }))}
                                    placeholder="∞"
                                />
                            </div>

                            <div className="tavField">
                                <label>Date début</label>
                                <input
                                    type="date"
                                    value={filters.dateDebut}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))}
                                />
                            </div>

                            <div className="tavField">
                                <label>Date de fin</label>
                                <input
                                    type="date"
                                    value={filters.dateFin}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="tavCard tavCard--noPad">
                    <div className="tavTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tavTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tavCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tavTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tavEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tavTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Type de demande</th>
                                    <th>Montant demandé</th>
                                    <th>Remboursement</th>
                                    <th>Date de la demande</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        className={demande.urgent ? "tavRowUrgent" : ""}
                                        onClick={() => showDetails(demande)}
                                        title={demande.urgent ? "Demande urgente" : ""}
                                    >
                                        <td>
                                            <span className="tavPrimary">{demande.employeeMatricule || "N/A"}</span>
                                        </td>

                                        <td>
                                            <div className="tavName">
                                                <span className="tavPrimary">{demande.employeeName}</span>
                                                {demande.urgent && <span className="tavFire">🔥</span>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavType">
                                                <span className="tavDot" style={{ backgroundColor: "#3b82f6" }} />
                                                <div>
                                                    <div className="tavPrimary">{demande.avanceType}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavAmount">
                                                <div className="tavAmountMain">{formatMontant(demande.montantDemande)} dhs</div>
                                                {demande.tauxInteret > 0 && (
                                                    <div className="tavSub">Taux: {demande.tauxInteret}%</div>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavPrimary">{demande.nombreMensualites} mois</div>
                                            <div className="tavSub">{formatMontant(demande.montantMensuel)} dhs/mois</div>
                                        </td>

                                        <td>
                                            <span className="tavMuted">{formatDate(demande.createdAt)}</span>
                                        </td>

                                        <td>{getStatutBadge(demande.status)}</td>

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            {demande.status === "PENDING_MANAGER" ? (
                                                <div className="tavRowActions">
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--ok"
                                                        onClick={() => handleApprove(demande.id)}
                                                        title="Approuver"
                                                        disabled={loading}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--no"
                                                        onClick={() => handleReject(demande)}
                                                        title="Refuser"
                                                        disabled={loading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="tavDetailsBtn"
                                                    onClick={() => showDetails(demande)}
                                                >
                                                    Détails
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL - identique à la version admin */}
            {showDetailsModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon">
                                    <CreditCard size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="tavModal__sub">Demande du {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavGrid2">
                                {/* Infos collaborateur */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <User size={16} /> Informations collaborateur
                                    </div>

                                    <div className="tavKv">
                                        <span>Matricule</span>
                                        <b>{selectedDemande.employeeMatricule || "N/A"}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Nom</span>
                                        <b>{selectedDemande.employeeName}</b>
                                    </div>
                                </div>

                                {/* Détails demande */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <CreditCard size={16} /> Détails de la demande
                                    </div>

                                    <div className="tavKv">
                                        <span>Type</span>
                                        <b>{selectedDemande.avanceType}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Montant demandé</span>
                                        <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Mensualités</span>
                                        <b>{selectedDemande.nombreMensualites} mois</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Taux d'intérêt</span>
                                        <b>{selectedDemande.tauxInteret || 0}%</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Mensualité</span>
                                        <b>{formatMontant(selectedDemande.montantMensuel)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Total à rembourser</span>
                                        <b>{formatMontant(selectedDemande.montantTotal)} dhs</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tavPanel tavPanel--full">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tavText">{selectedDemande.motif || " - "}</div>
                                </div>

                                {/* Justificatifs */}
                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="tavPanel">
                                        <div className="tavPanel__title">
                                            <FileText size={16} /> Justificatifs fournis
                                        </div>
                                        <div className="tavTags">
                                            {selectedDemande.justificatifs.map((doc, idx) => (
                                                <span key={idx} className="tavTag">
                                                    {doc.split('/').pop()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tavModal__footer">
                            {selectedDemande.status === "PENDING_MANAGER" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedDemande);
                                        }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>

                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedDemande.id);
                                            closeDetails();
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>

                                    <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL - identique */}
            {showRejectModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel tavModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon tavModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Refuser la demande d'avance/prêt</div>
                                    <div className="tavModal__sub">
                                        {selectedDemande.employeeName} — {selectedDemande.avanceType}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeReject} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavMiniCard">
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Montant demandé</span>
                                    <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                </div>
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Remboursement</span>
                                    <b>
                                        {selectedDemande.nombreMensualites} mois × {formatMontant(selectedDemande.montantMensuel)} dhs
                                    </b>
                                </div>
                            </div>

                            <label className="tavLabel">
                                Motif du rejet <span className="tavReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette demande..."
                                className="tavTextarea"
                            />
                            <div className="tavHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tavModal__footer">
                            <button type="button" className="tavBtn tavBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>

                            <button
                                type="button"
                                className="tavBtn tavBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le rejet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementAvancesManager;
